import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import Spinner from './Components/Spinner';

function DealerInventory() {
  const { dealerId } = useParams();
  const [dealer, setDealer] = useState(null);
  const [cars, setCars] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Get values from URL
  const view = searchParams.get('view') || 'all';
  const sort = searchParams.get('sort') || 'recent';
  const keyword = searchParams.get('keyword') || '';
  const minYear = searchParams.get('minYear') || '';
  const maxYear = searchParams.get('maxYear') || '';

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE}/cars/dealer/${dealerId}/inventory`)
      .then(res => res.json())
      .then(data => {
        setDealer(data.dealer);
        setCars(data.cars);
      });
  }, [dealerId]);

  const updateParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  if (!cars || !dealer) return <Spinner />;

  // Apply filters
  const filteredCars = cars.filter(car => {
    if (view === 'active' && car.soldDate) return false;
    if (view === 'sold' && !car.soldDate) return false;
    if (keyword && !`${car.make} ${car.model} ${car.trim}`.toLowerCase().includes(keyword.toLowerCase()))
      return false;
    if (minYear && car.year < parseInt(minYear)) return false;
    if (maxYear && car.year > parseInt(maxYear)) return false;
    return true;
  });

  // Apply sorting
  const sortedCars = [...filteredCars].sort((a, b) => {
    if (sort === 'price_asc') return a.price - b.price;
    if (sort === 'price_desc') return b.price - a.price;
    if (sort === 'year_desc') return b.year - a.year;
    if (sort === 'mileage_asc') return a.mileage - b.mileage;
    return new Date(b.insertDate) - new Date(a.insertDate);
  });

  return (
    <div style={{ maxWidth: "1100px", margin: "2rem auto", padding: "1rem" }}>
      <h1 className="text-2xl font-bold mb-2">Dealer Inventory</h1>
      <div className="mb-6 bg-gray-100 rounded-lg p-4 shadow">
        <h1 className="text-2xl font-bold">{dealer.name}</h1>
        <div className="text-sm text-gray-700">
          {dealer.address}, {dealer.city}, {dealer.state} {dealer.zip}
        </div>
        {dealer.phone && (
          <div className="text-sm text-gray-600 mt-1">📞 {dealer.phone}</div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select value={view} onChange={(e) => updateParam('view', e.target.value)} className="border px-2 py-1 text-sm rounded">
          <option value="all">All Listings</option>
          <option value="active">Active Only</option>
          <option value="sold">Sold Only</option>
        </select>

        <select value={sort} onChange={(e) => updateParam('sort', e.target.value)} className="border px-2 py-1 text-sm rounded">
          <option value="recent">Most Recent</option>
          <option value="price_asc">Price ↑</option>
          <option value="price_desc">Price ↓</option>
          <option value="year_desc">Year ↓</option>
          <option value="mileage_asc">Mileage ↑</option>
        </select>

        <input
          type="text"
          value={keyword}
          onChange={(e) => updateParam('keyword', e.target.value)}
          placeholder="Search make/model"
          className="border px-2 py-1 text-sm rounded w-40"
        />

        <input
          type="number"
          value={minYear}
          onChange={(e) => updateParam('minYear', e.target.value)}
          placeholder="Min Year"
          className="border px-2 py-1 text-sm rounded w-24"
        />

        <input
          type="number"
          value={maxYear}
          onChange={(e) => updateParam('maxYear', e.target.value)}
          placeholder="Max Year"
          className="border px-2 py-1 text-sm rounded w-24"
        />
      </div>

      {/* Count */}
      <div className="mb-4 text-sm text-gray-700 font-medium">
        Showing {sortedCars.length} result{sortedCars.length !== 1 ? 's' : ''}
      </div>

      {/* Car Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedCars.map(car => (
          <div key={car.id} className="border rounded shadow p-4 bg-white hover:shadow-md relative">
            {car.soldDate && (
              <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded shadow">
                SOLD
              </span>
            )}
            <img
              src={car.imageUrl}
              alt={`${car.year} ${car.make} ${car.model}`}
              className="w-full h-40 object-cover mb-2 rounded"
            />
            <div className="font-semibold text-sm">
              {car.year} {car.make} {car.model} {car.trim}
            </div>
            <div className="text-gray-600 text-sm">{car.mileage.toLocaleString()} miles</div>
            <div className={`${car.soldDate ? 'text-gray-500 line-through' : 'text-green-600'} font-bold mt-1`}>
              ${car.price.toLocaleString()}
            </div>
            <Link to={`/car/${car.id}`} className="text-blue-600 text-sm mt-2 block">
              View Details →
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DealerInventory;
